import { AnimatePresence, motion } from "framer-motion";
import { ShareNetwork, EnvelopeSimple } from "@phosphor-icons/react";
import { useTranslations } from "@/i18n/utils";

import { useAnimationState } from "@/hooks/useAnimationState";

import type { Artist } from "@/types/artist";

import { shareAction } from "@/lib/shareUtils";

import { useIsMobile } from "@/utils/animation";

import Button from "@/components/shared/buttons/button";
import { LanguageSelector } from ".";
import { JoinButton } from "./JoinButton";
import EmailButton from "@/components/shared/buttons/EmailButton";

interface RightSectionProps {
  isScrolled: boolean;
  lang: "en" | "es";
  astroUrl: URL;
  artistData: Artist;
}

export const RightSection = ({
  isScrolled,
  lang,
  astroUrl,
  artistData,
}: RightSectionProps) => {
  const text = artistData.Roles.map((genre) => genre.name).join(", ");
  const title = `${artistData.profileName} - ${text} - My MavelPoint`;
  const url = astroUrl.pathname;

  const isMobile = useIsMobile();
  const { scrollDirection } = useAnimationState(isScrolled);
  const t = useTranslations(lang);
  const buttonLabel = t("publicProfile.button.contact").toString();

  const renderContent = (isScrolled: boolean) => (
    <div className="flex items-center gap-x-3 md:gap-x-6">
      <LanguageSelector
        lang={lang}
        pathname={url}
        classNameListBoxButton="p-0"
        size="sm"
      />

      {isScrolled && (
        <>
          <Button
            buttonType="tertiary"
            size="lg"
            color="white"
            leftIcon={ShareNetwork}
            onClick={() => shareAction({ title, url })}
            weightIcon="bold"
            className="col-span-1 hidden md:grid"
          />
          {artistData.agencyEmail && (
            <EmailButton
              buttonType="primary"
              buttonSize="lg"
              color="white"
              label={buttonLabel}
              className="col-span-1 hidden md:flex"
              email={artistData.agencyEmail}
            />
          )}
        </>
      )}

      <JoinButton lang={lang} />
      {artistData.agencyEmail && (
        <EmailButton
          buttonType="tertiary"
          buttonSize="sm"
          color="white"
          weightIcon="bold"
          leftIcon={EnvelopeSimple}
          className="col-span-1 md:hidden"
          email={artistData.agencyEmail}
        />
      )}
    </div>
  );

  if (isMobile) {
    return renderContent(isScrolled);
  }

  return (
    <div className="flex items-center gap-x-3 md:gap-x-6">
      <AnimatePresence mode="wait">
        <motion.div
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -30 }}
          transition={{
            duration: 0.2,
            ease: scrollDirection === "down" ? "backIn" : "backOut",
            delay: scrollDirection === "down" ? 0.2 : 0,
          }}
          className="flex items-center gap-x-3 md:gap-x-6"
        >
          {renderContent(isScrolled)}
        </motion.div>
      </AnimatePresence>
    </div>
  );
};
